import { createContext, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { getAttributionPayload } from "../lib/attribution";
import { fireConversion, setEnhancedConversionData } from "../lib/tracking";
import { trackEvent } from "../lib/trackingClient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ChatContext = createContext(null);

export function useChat() {
  return useContext(ChatContext);
}

export function ChatProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [captured, setCaptured] = useState(false);
  const starting = useRef(false);

  const start = useCallback(async () => {
    if (sessionId || starting.current) return;
    starting.current = true;
    try {
      const { data } = await axios.post(`${API}/chat/start`, {
        attribution: getAttributionPayload(),
      });
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", content: data.greeting }]);
      trackEvent("chat_start", { session_id: data.session_id });
    } catch (e) {
      setMessages([
        { role: "assistant", content: "Entschuldigung, der Berater ist gerade nicht erreichbar. Bitte versuchen Sie es gleich erneut." },
      ]);
    } finally {
      starting.current = false;
    }
  }, [sessionId]);

  const openChat = useCallback(() => {
    setOpen(true);
    start();
  }, [start]);

  const send = useCallback(
    async (text) => {
      if (!text.trim() || !sessionId || loading) return;
      const userMsg = { role: "user", content: text };
      setMessages((m) => [...m, userMsg]);
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/chat/message`, {
          session_id: sessionId,
          message: text,
          attribution: getAttributionPayload(),
        });
        setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        if (data.lead_captured && !captured) {
          setCaptured(true);
          if (data.contact) {
            await setEnhancedConversionData({
              email: data.contact.email,
              phone: data.contact.phone,
            });
          }
          fireConversion("chat_lead");
          trackEvent("chat_lead", { session_id: sessionId, value: 100 });
        }
      } catch (e) {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: "Es gab ein technisches Problem. Bitte versuchen Sie es erneut." },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [sessionId, loading, captured]
  );

  return (
    <ChatContext.Provider
      value={{ open, setOpen, openChat, sessionId, messages, loading, send, start, captured }}
    >
      {children}
    </ChatContext.Provider>
  );
}
